// ── <RedeemCopyRoute> — the `/copy/:id` screen ──
//
// The one route in this SPA, and the one piece that owns a URL: a copy link is a
// URL someone else sends you, so it has to render before (and instead of) the
// dashboard. App.jsx picks it with `matchCopyRoute` and renders it in place of
// `AppInner`, which is deliberate rather than incidental — see the two notes below.
//
// 1. IT MUST BYPASS `AppInner`, NOT LIVE INSIDE IT. `AppInner` performs the
//    unauthenticated bounce DURING RENDER: `authEnabled && !me` calls
//    `window.location.replace("/auth/login?returnTo=" + pathname + search)` and
//    returns early (App.jsx:4867-4877). An arriving recipient is exactly that case,
//    so a `<RedeemCopyRoute>` mounted inside `AppInner` would never mount at all —
//    the fragment would be discarded by a redirect that happened before any effect
//    of ours could park the secret, and the link would be unredeemable. Mounting
//    outside means the preview POST's own 401 drives the login round trip instead,
//    through lib/apiClient, AFTER the secret is stashed. If anyone ever moves this
//    render site inside `AppInner`, that breaks silently: the copy still "works"
//    for an already-signed-in tester and fails for every fresh recipient.
//
// 2. THEREFORE NO `--c-*` VARIABLES HERE. `GlobalStyles` — which publishes the
//    whole `--c-*` set — is rendered inside `AppInner`, so on this screen those
//    variables do not exist and `C.bg` (`var(--c-bg)`) resolves to nothing. Colours
//    come from `themeColors("dev")`, the raw hex for the app's default theme, the
//    same reason `TosGate` carries a raw-hex palette (TosGate.jsx:11-14). The
//    user's chosen theme lives behind `useTheme` inside `AppInner` and is not
//    reachable from here; a transient "opening your copy" screen in the default
//    palette is the accepted cost. Note `dev` is deliberately near-monochrome — its
//    `red` is a grey (#b0b0b0) — so the failure states are carried by their WORDING,
//    not by colour. That is the theme's own choice, and inventing a red the active
//    theme suppresses everywhere else would look like a different application.
//
// 3. IT STILL HAS TO LOOK LIKE A CHATBOARD POPUP. `.modal-shell-panel`
//    (GlobalStyles.jsx:98) is the house chrome — `var(--c-panel)`, a 2px accent
//    border, `border-radius: 16px` — and this screen reproduces it in raw hex, with
//    `SendCopyModal`'s panel geometry, headline and button styling alongside. Two
//    consequences worth stating because they look like oversights: the border is
//    ACCENT on every phase (failures included — the house popups do not recolour
//    their chrome, they colour their text), and the buttons keep one geometry with
//    only the fill separating primary from secondary.
//
// THIS SCREEN IS A DECISION, NOT A NOTIFICATION. The arrival previews the link and
// then WAITS: the recipient sees which forecast, from whom, and chooses ACCEPT or
// REJECT. Nothing is created until ACCEPT.
//
// AFTERWARDS, THE TWO SUCCESSES ARE DELIBERATELY DIFFERENT — see the button block
// below, and the hook's header for the reasoning. A FRESH copy carries no `OK` and the
// hook hands off by itself (consent was the ACCEPT press a moment earlier); the
// ALREADY-HAD screen keeps `OK`, because it carries news and auto-navigating past that
// unread is what made its wording pointless before. So `BODIES.done` must never name a
// control, and `ALREADY_BODY` is the only line that does.
//
// Every button here is wired to a handler and rendered only when that handler exists;
// a dead control is worse than no control.
//
// AND A SCREEN GETS A BUTTON ONLY IF THERE IS A CHOICE LEFT. The three screens that
// follow an action the recipient took — `done` fresh (ACCEPT), `declined` (REJECT), and
// nothing else — leave on their own, because asking someone to confirm a decision they
// just made is a click for nothing. The two REFUSALS keep [DISMISS]: there the recipient
// chose nothing, the outcome is not what they wanted, and how long to sit with it is
// theirs. The already-had `done` keeps [OK] for the separate reason below (it carries
// news).
//
// Everything stateful is in hooks/useRedeemCopy.js — see that file's header for why
// (an effect inside a component is untestable under `renderToStaticMarkup`). What
// is left here is `RedeemCopyScreen`, a pure-props presentational component, plus
// the glue that wires the hook's four actions to it.
//
// WORDING: the noun is a FORECAST COPY, granted by a FORECAST COPY LINK — never a
// bare "copy" on any of these screens (see HEADINGS for why). That now holds for the
// SERVER's strings too: a refusal renders the API's own `{error}`, and the four that
// reach a recipient — 410 inactive, 409 unavailable, 404 not-found, 403 invalid —
// carry the full noun as of this change (server/handlers/copyLinks.js:39). The
// 400-validation bodies deliberately do not: they name a request field, not the
// artifact.
//
// And the COPY verb, never "share" (A2). The string "shar" must not appear in
// anything this file renders, and no copy here may imply the sender can take a copy
// back — a redeemed copy is the recipient's permanently, beyond the owner's reach.
// Nor may any failure hint at a partial copy: every refusal happens before or
// inside the one transaction, so a failed redeem leaves nothing behind (§7.2). The
// declined screen carries its own version of the same rule in the other direction:
// declining is local and silent, so it must not claim the link is now dead.

import React from "react";
import { themeColors } from "../lib/colors.js";
import { redeemedForecastPath } from "../lib/copyLinks/route.js";
import { expiryText } from "../lib/copyLinks/state.js";
import { useRedeemCopy } from "../hooks/useRedeemCopy.js";
import { surface, ui } from "../lib/telemetry/instrument.js";

const T = themeColors("dev");
// The app font for running text; this route renders outside AppInner, so it names it.
const MONO = '"IBM Plex Sans",system-ui,sans-serif';

/**
 * Headline per phase. Literal strings so a test pins the wording, not a lookup.
 *
 * THE NOUN IS A "FORECAST COPY", and the thing that grants one is a "FORECAST COPY
 * LINK" — never a bare "copy", anywhere on these screens. Alone, the word reads as a
 * clipboard copy or a generic duplicate, and this recipient is deciding whether to
 * take a FORECAST into their own list. The qualifier is what says what they are
 * getting, so a headline that grows too long gets restructured rather than shortened
 * by dropping it.
 *
 * "Forecast copy" still satisfies A2 — it contains the copy verb and says nothing
 * about sharing.
 */
const HEADINGS = {
  opening: "Opening this forecast copy link…",
  offer: "Accept this forecast copy?",
  creating: "Making your forecast copy…",
  done: "Your forecast copy is ready",
  declined: "You declined this forecast copy",
  "needs-link": "This forecast copy link is incomplete",
  failed: "Couldn't open this forecast copy link",
};

/** The `done` variant for a forecast copy the recipient already owned when they
 *  arrived. */
const ALREADY_HEADING = "You already have this forecast copy";

/**
 * The line under the headline, per phase.
 *
 * `opening` deliberately says nothing has been created yet: the first POST is the
 * write-nothing preview, and the older wording ("making your own copy") described a
 * request that had not been sent.
 *
 * `done` is the FRESH success, which has no `OK` — the hook hands off by itself there
 * (see the button block below), so this line must not point at a control that is not
 * on the screen. `ALREADY_BODY` is the shape that keeps the button, and it is the only
 * one that names it.
 */
const BODIES = {
  opening: "Checking what this forecast copy link offers. Nothing has been created yet.",
  offer:
    "You have received a forecast copy link. Your forecast copy is created only when " +
    "you accept.",
  creating: "Creating your forecast copy. This takes a moment.",
  done: "Your forecast copy is in your forecast list. Taking you to it now.",
  // Ends by announcing its own exit: this screen leaves on a timer
  // (DECLINE_DISMISS_MS), and a page that navigates with no warning reads as a bug
  // rather than as a courtesy. Same shape as `done`, for the same reason.
  declined:
    "No forecast copy was created, and nothing was recorded. This forecast copy link still " +
    "works — if you change your mind, open it again from wherever you received it. Taking " +
    "you back to your forecasts.",
};

/** The `done` body for a forecast copy that already existed — see ALREADY_HEADING. */
const ALREADY_BODY =
  "You accepted this forecast copy link before. Your forecast copy is already in your " +
  "forecast list — OK takes you to it.";

/** Shared style for the two dark, low-emphasis lines of detail. */
const DETAIL_LINE = { fontSize: 12, lineHeight: 1.6, color: T.grayL, marginTop: 6 };

/**
 * Button style, matching `SendCopyModal`'s submit button so the two ends of one
 * feature look like one feature (SendCopyModal.jsx:292-311).
 *
 * The geometry is identical for both roles — same padding, radius, size, tracking —
 * and ONLY the fill differs, so the primary is unmistakably primary without the
 * secondary shrinking or moving. `primary` is the proceed action (ACCEPT, OK, TRY
 * AGAIN); the secondary is REJECT.
 *
 * The secondary's `btnBg`/`gray` pair is the colouring that modal uses for its
 * DISABLED submit, deliberately reused here for an enabled control — so keep
 * `cursor: "pointer"` and never pass a `disabled` attribute alongside it, or the
 * button becomes indistinguishable from one that cannot be pressed.
 *
 * No margin here: the callers place buttons in a flex row (see ACTION_ROW).
 */
function buttonStyle(primary) {
  return {
    background: primary ? T.action : T.btnBg,
    color: primary ? T.bg : T.gray,
    border: "none",
    borderRadius: 8,
    padding: "9px 14px",
    fontFamily: MONO,
    fontSize: 13,
    letterSpacing: 0,
    fontWeight: 500,
    cursor: "pointer",
  };
}

/**
 * The action row: normal flow at the end of the panel content, primary first, no
 * right-alignment — the same shape as `SendCopyModal`'s submit and the action row in
 * `CopyLinksPanel`. `flexWrap` because two mono buttons at 92vw can outgrow a narrow
 * phone.
 */
// Right-aligned, as chatboard's dialogs are: the confirming action sits at the
// panel's trailing edge. `flexWrap` stays so a narrow viewport stacks the pair
// rather than clipping one, and `flex-end` keeps them trailing when it does.
const ACTION_ROW = {
  display: "flex",
  gap: 10,
  marginTop: 14,
  flexWrap: "wrap",
  justifyContent: "flex-end",
};

/**
 * The screen. Pure props — no store reads, no fetch, no window
 * (components/ convention).
 *
 * @param {object} props
 * @param {"opening"|"offer"|"creating"|"done"|"declined"|"needs-link"|"failed"} props.phase
 * @param {{question: string|null, ownerLabel: string|null, expiresAt: string|null,
 *   includeChat: boolean}|null} [props.details]  what the preview said, already
 *   normalised by the hook. Null before it answers, and on every refusal.
 * @param {boolean} [props.already]  the copy existed before this visit, so `done` says
 *   so instead of claiming to have just made it.
 * @param {string|null} [props.message]  already user-facing (the hook flattens the
 *   server's `{error}` and its own copy into one string)
 * @param {boolean} [props.retryable]  offer a retry control. False for every
 *   refusal that cannot become a success, so the button is never a lie.
 * @param {(() => void)|null} [props.onRetry]
 * @param {(() => void)|null} [props.onAccept]  create the copy
 * @param {(() => void)|null} [props.onReject]  decline, locally and silently
 * @param {(() => void)|null} [props.onOpen]  the [OK] hand-off — to the recipient's own
 *   forecast copy, and only from a success
 * @param {(() => void)|null} [props.onDismiss]  the [DISMISS] way out of a terminal
 *   screen that has no copy to open — the two refusals and `declined`
 * @param {number} [props.now]  epoch ms, injected so the expiry text stays clock-free.
 */
export function RedeemCopyScreen({
  phase,
  details = null,
  already = false,
  message = null,
  retryable = false,
  onRetry = null,
  onAccept = null,
  onReject = null,
  onOpen = null,
  onDismiss = null,
  now,
}) {
  // An unrecognised phase reads as "failed" rather than falling through to
  // "opening": a spinner that never resolves is the worse of the two wrong answers.
  const known = Object.hasOwn(HEADINGS, phase) ? phase : "failed";
  const bad = known === "needs-link" || known === "failed";
  // A REFUSAL NEEDS A WAY OUT, and only a refusal. Both end the journey with no forecast
  // copy to open, so without a control the recipient is parked on a spent `/copy/:id`
  // URL with nothing to press — and how long they sit with a message about an outcome
  // they did not choose is theirs to decide, hence a button rather than a timer.
  //
  // The other two terminal screens leave BY THEMSELVES, each because the recipient has
  // already acted: `declined` on the hook's timer (DECLINE_DISMISS_MS) after REJECT, and
  // `done` on the hook's hand-off after ACCEPT — except the already-had variant, which
  // carries news and so keeps [OK].
  const dismissible = bad;
  const heading = known === "done" && already ? ALREADY_HEADING : HEADINGS[known];
  const body = known === "done" && already ? ALREADY_BODY : BODIES[known];
  // What the recipient is being offered, or what they now have. Withheld on
  // `declined` (the offer is over) and on every refusal (there is nothing to show —
  // a preview that failed returned no details).
  const showDetails = !!details && (known === "offer" || known === "creating" || known === "done");
  // The expiry bears on the DECISION and nowhere else: a countdown beside a copy that
  // already exists reads as a deadline on the copy, which is not a thing.
  const deciding = known === "offer" || known === "creating";

  return (
    <div
      data-testid="redeem-copy"
      {...surface("copy-redeem")}
      data-redeem-phase={known}
      // The offer names the forecast's question and its owner: never recorded.
      data-telemetry-redact=""
      style={{
        width: "100vw",
        minHeight: "100vh",
        background: T.bg,
        color: T.white,
        fontFamily: MONO,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        boxSizing: "border-box",
      }}
    >
      {/*
        The house popup panel, in raw hex: `.modal-shell-panel`
        (GlobalStyles.jsx:98) is `var(--c-panel)` / `2px solid var(--c-accent)` /
        `border-radius: 16px`, and the geometry is `SendCopyModal`'s `panelStyle`
        (SendCopyModal.jsx:171-181).

        THE BORDER IS ACCENT ON EVERY PHASE, failures included, because that is what
        the house popups do — `SendCopyModal` keeps its accent border and colours only
        the error text. It also loses nothing here: this palette's `red` is a grey
        (#b0b0b0), so the per-phase red border it replaces was nearly invisible, and
        the failure signal was always carried by the wording (see the header).
      */}
      <div
        style={{
          width: 520,
          maxWidth: "92vw",
          background: T.panel,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: 24,
          boxSizing: "border-box",
        }}
      >
        {/* `SendCopyModal`'s h1, verbatim (SendCopyModal.jsx:190-200) — including the
            white on every phase. See the panel note above for why a failure is not
            recoloured. */}
        <div
          style={{
            fontFamily: MONO,
            fontSize: 18,
            fontWeight: 600,
            color: T.white,
            marginBottom: 12,
          }}
        >
          {heading}
        </div>

        {!bad && (
          <div
            data-testid="redeem-copy-body"
            style={{ fontSize: 12, lineHeight: 1.6, color: T.grayL }}
          >
            {body}
          </div>
        )}

        {showDetails && (
          <div
            data-testid="redeem-copy-details"
            style={{
              marginTop: 12,
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              background: T.sectionBg,
              padding: "10px 12px",
            }}
          >
            {/* The question, when the envelope carried one. Quoted rather than
                labelled, because it is the sender's words verbatim. */}
            {details.question && (
              <div
                data-testid="redeem-copy-question"
                style={{ fontSize: 12, lineHeight: 1.5, color: T.white }}
              >
                &ldquo;{details.question}&rdquo;
              </div>
            )}
            {/* An older record has no owner label at all (the field postdates it), and
                that is stated rather than papered over with a placeholder name — the
                recipient is deciding partly on WHO sent this. */}
            <div data-testid="redeem-copy-from" style={DETAIL_LINE}>
              {details.ownerLabel
                ? `Sent by ${details.ownerLabel}`
                : "The sender is not recorded on this forecast copy link."}
            </div>
            {/* Said in both directions on purpose: "no conversation" is information,
                and its absence would read as an omission. */}
            <div data-testid="redeem-copy-chat" style={DETAIL_LINE}>
              {details.includeChat
                ? "Includes the sender’s conversation about this forecast."
                : "The sender’s conversation is not included."}
            </div>
            {deciding && (
              <div data-testid="redeem-copy-expiry" style={DETAIL_LINE}>
                {/* `details` carries no `state`: preview answers 410 for a link that
                    is not live, so anything reaching this screen is live by
                    construction — and expiryText answers "Unknown" for a state it
                    does not recognise (lib/copyLinks/state.js:127). */}
                {expiryText({ state: "live", expiresAt: details.expiresAt }, now)}
              </div>
            )}
          </div>
        )}

        {message && (
          <div
            data-testid="redeem-copy-message"
            style={{ fontSize: 12, lineHeight: 1.6, color: T.white, marginTop: bad ? 0 : 10 }}
          >
            {message}
          </div>
        )}

        {/* The decision. Both controls or neither — an ACCEPT with no way to decline
            is the screen this replaced. */}
        {known === "offer" && onAccept && onReject && (
          <div style={ACTION_ROW}>
            {/* REJECT first in SOURCE order, so the row reads reject-then-accept and
                the primary sits at the right edge — the position a dialog's
                confirming action holds. It also means the destructive-ish choice is
                never the one under the cursor's resting place. */}
            <button
              type="button"
              data-testid="redeem-copy-reject"
              {...ui("copy-redeem.reject")}
              onClick={onReject}
              style={buttonStyle(false)}
            >
              Decline
            </button>
            <button
              type="button"
              data-testid="redeem-copy-accept"
              {...ui("copy-redeem.accept")}
              onClick={onAccept}
              style={buttonStyle(true)}
            >
              Accept
            </button>
          </div>
        )}

        {/*
          The hand-off, and it exists for ONE of the two successes.

          A FRESH copy needs no button: the recipient just pressed ACCEPT, so the
          consent has already been given and arriving at the copy is what they asked
          for — a second click to confirm what they chose a moment ago is friction,
          not safety. That screen is transitional and the hook navigates on its own.

          An ALREADY-HAD copy keeps `OK`, because there the screen carries the news:
          nothing was created, and the copy predates this visit. Auto-navigating
          would flash that past unread, which is what made the wording pointless
          before the button existed.
        */}
        {known === "done" && already && onOpen && (
          <div style={ACTION_ROW}>
            <button
              type="button"
              data-testid="redeem-copy-ok"
              {...ui("copy-redeem.open")}
              onClick={onOpen}
              style={buttonStyle(true)}
            >
              OK
            </button>
          </div>
        )}

        {/*
          THE WAY OUT, and — when the refusal can still become a success — the way to
          try again, in ONE row rather than two stacked ones.

          TRY AGAIN appears only on a refusal that can plausibly turn into a success: a
          recorded generation catching up, a plane getting its config, a network coming
          back. A revoked link, an expired one, or a bad secret gets none, because
          offering one would promise a retry that cannot work.

          DISMISS appears on all three terminal screens (see `dismissible`), and its
          emphasis is positional rather than fixed: PRIMARY when it is the only control
          — a lone button in the secondary fill reads as disabled, since that pair is
          `SendCopyModal`'s disabled colouring — and SECONDARY beside TRY AGAIN, which
          is then the proceed action and holds the trailing edge.
        */}
        {((dismissible && onDismiss) || (retryable && onRetry)) && (
          <div style={ACTION_ROW}>
            {dismissible && onDismiss && (
              <button
                type="button"
                data-testid="redeem-copy-dismiss"
                {...ui("copy-redeem.dismiss")}
                onClick={onDismiss}
                style={buttonStyle(!(retryable && onRetry))}
              >
                Close
              </button>
            )}
            {retryable && onRetry && (
              <button
                type="button"
                data-testid="redeem-copy-retry"
                {...ui("copy-redeem.retry")}
                onClick={onRetry}
                style={buttonStyle(true)}
              >
                Try again
              </button>
            )}
          </div>
        )}

        {/*
          THE DISCLAIMER IS ON THE OFFER SCREEN AND NOWHERE ELSE. It answers "what am I
          agreeing to" — that the forecast copy is the recipient's own forecast, that the
          sender cannot take it back, and that it stays live-linked — so it belongs
          beside the decision it informs. On every other screen the decision is already
          taken (or was never available), the recipient has seen it once, and repeating
          it is noise: `done` in both shapes, `declined`, `creating`, and the two
          refusals all drop it.
        */}
        {known === "offer" && (
          <div
            data-testid="redeem-copy-disclaimer"
            style={{ fontSize: 11, lineHeight: 1.6, color: T.gray, marginTop: 16 }}
          >
            A forecast copy is yours to keep: your own forecast of the same question, which you can
            edit and delete. It stays live-linked, so the sender&rsquo;s reforecasts and the
            eventual resolution reach your forecast copy too.
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The route: preview on mount, offer the copy, and land on it when the recipient
 * presses [OK].
 *
 * @param {object} props
 * @param {string} props.copyId  the `:id` path segment, verbatim and unvalidated
 * @param {object} [props.win]  window (injected)
 * @param {Function} [props.apiPostImpl]  injectable for tests
 * @param {(forecastId: string|null) => void} [props.onRedeemed]  defaults to
 *   navigating; a caller may override to route in-app instead. Fired by [OK] only.
 * @param {object|null} [props.storage]  injectable sessionStorage
 */
export default function RedeemCopyRoute({
  copyId,
  win = typeof window === "undefined" ? null : window,
  apiPostImpl,
  onRedeemed,
  storage,
}) {
  // `replace`, not `assign`: `/copy/:id` is a spent one-time URL, and leaving it in
  // history means a Back press re-enters this route with no secret and shows the
  // "incomplete link" screen for a copy the person already owns.
  // BOTH ways off this route go through here, so the `replace`-not-`assign` decision and
  // the injected-window guard exist once. Two callers with the same four-line body is how
  // one of them later grows an `assign` and puts a spent /copy/:id back in history.
  const leaveTo = React.useCallback(
    (path) => {
      if (win && win.location && typeof win.location.replace === "function") {
        win.location.replace(path);
      }
    },
    [win],
  );
  const navigate = React.useCallback(
    (forecastId) => leaveTo(redeemedForecastPath(forecastId)),
    [leaveTo],
  );
  // DISMISS DOES NOT GO THROUGH `onRedeemed`. That callback means "a forecast copy
  // exists and here is its id" — a caller may override it to route in-app — so firing
  // it from a refusal would report a redemption that never happened. Dismiss is plain
  // navigation to the dashboard, needs no hook state, and so lives here rather than in
  // `useRedeemCopy`.
  //
  // It names "/" outright rather than calling `navigate(null)`, which lands in the same
  // place: `redeemedForecastPath` is the REDEEMED-COPY deep link, and routing a refusal
  // through it would make this screen's destination an emergent property of how that
  // codec happens to treat a null id. The shared part is the mechanism above, not the
  // meaning.
  const dismiss = React.useCallback(() => leaveTo("/"), [leaveTo]);
  const { phase, details, already, message, retryable, accept, reject, openCopy, retry } =
    useRedeemCopy({
      copyId,
      win,
      apiPostImpl,
      onRedeemed: onRedeemed || navigate,
      // The same leaving action serves both callers: the [DISMISS] button on a refusal,
      // and the declined screen's timer inside the hook.
      onDismiss: dismiss,
      storage,
    });
  return (
    <RedeemCopyScreen
      phase={phase}
      details={details}
      already={already}
      message={message}
      retryable={retryable}
      onRetry={retry}
      onAccept={accept}
      onReject={reject}
      onOpen={openCopy}
      onDismiss={dismiss}
    />
  );
}
